import { useCallback, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { CreateTenantRequest } from '@order-pilot/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { SelectField, TextAreaField, TextField } from '@/components/Form';
import { IconArrowLeft } from '@/components/Icons';
import { PageHeader } from '@/components/PageHeader';
import { useIsOwner } from '@/auth/role';
import { LogoPicker } from '@/features/tenants/LogoPicker';
import { createTenant, tenantKeys, uploadTenantLogo } from '@/features/tenants/api';
import { getErrorMessage } from '@/lib/api';
import { fetchPlans, planKeys } from '@/features/plans/api';
import { IANA_TIMEZONES } from '@/lib/timezones';
import { compactErrors, requiredText } from '@/lib/validate';

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SAR', 'MYR', 'SGD'] as const;

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

interface FormState {
  name: string;
  phone: string;
  address: string;
  timezone: string;
  currency: string;
  planId: string;
}

type FormField = keyof FormState;

const INITIAL_STATE: FormState = {
  name: '',
  phone: '',
  address: '',
  timezone: browserTimezone(),
  currency: 'INR',
  // Deliberately empty: a plan is chosen, never inherited from whichever
  // tier happens to sort first.
  planId: '',
};

export function NewTenantPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const plansQuery = useQuery({
    queryKey: planKeys.list(),
    queryFn: ({ signal }) => fetchPlans(signal),
  });
  const [form, setForm] = useState<FormState>(INITIAL_STATE);
  const [errors, setErrors] = useState<Partial<Record<FormField, string>>>({});
  const [logoFile, setLogoFile] = useState<File | null>(null);
  // A logo is an owner's edit; an ISSUER creates the tenant without one.
  const isOwner = useIsOwner();

  const mutation = useMutation({
    mutationFn: (input: CreateTenantRequest) => createTenant(input),
    onSuccess: async (tenant) => {
      // The logo upload is best-effort here: the tenant already exists by
      // this point, so a failed upload should not look like a failed
      // creation. It's swallowed rather than surfaced because this page is
      // about to navigate away — there's nowhere left to show the error, and
      // the detail page's own logo control is where the SA would retry.
      if (logoFile) {
        await uploadTenantLogo(tenant.id, logoFile).catch(() => undefined);
      }
      void queryClient.invalidateQueries({ queryKey: tenantKeys.all });
      navigate(`/cp/tenants/${tenant.id}`, { replace: true });
    },
  });

  /** Applies a patch and clears the inline error for every field it touches. */
  const update = useCallback((patch: Partial<FormState>) => {
    setForm((current) => ({ ...current, ...patch }));
    setErrors((current) => {
      const touched = Object.keys(patch) as FormField[];
      if (!touched.some((field) => current[field])) return current;
      const next = { ...current };
      for (const field of touched) delete next[field];
      return next;
    });
  }, []);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const validation = compactErrors<FormField>({
        name: requiredText(form.name, 'Restaurant name'),
        timezone: requiredText(form.timezone, 'Timezone'),
        currency: requiredText(form.currency, 'Currency'),
      });
      setErrors(validation);
      if (Object.keys(validation).length > 0) return;

      const payload: CreateTenantRequest = {
        name: form.name.trim(),
        timezone: form.timezone.trim(),
        currency: form.currency.trim().toUpperCase(),
      };
      if (form.phone.trim()) payload.phone = form.phone.trim();
      if (form.address.trim()) payload.address = form.address.trim();
      // Empty means "no plan" — sent as null rather than omitted, so the
      // intent is explicit rather than looking like a forgotten field.
      payload.planId = form.planId === '' ? null : form.planId;

      mutation.mutate(payload);
    },
    [form, mutation],
  );

  return (
    <>
      <PageHeader
        eyebrow={
          <Link
            to="/cp/tenants"
            className="inline-flex items-center gap-1.5 rounded text-sm font-medium text-slate-500 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2"
          >
            <IconArrowLeft className="h-4 w-4" />
            Tenants
          </Link>
        }
        title="New tenant"
        description="Register a restaurant. Portal logins are added separately, from the tenant page — a licence-only tenant needs none."
      />

      <form onSubmit={handleSubmit} noValidate className="max-w-3xl space-y-6">
        {mutation.isError ? (
          <Alert tone="error" title="Could not create the tenant">
            {getErrorMessage(mutation.error)}
          </Alert>
        ) : null}

        <section className="card" aria-labelledby="section-restaurant">
          <div className="card-header">
            <div>
              <h2 id="section-restaurant" className="text-sm font-semibold text-slate-900">
                Restaurant
              </h2>
              <p className="mt-0.5 text-xs text-slate-500">
                Reporting uses the timezone and currency below — they are how POS dates are interpreted.
              </p>
            </div>
          </div>
          <div className="grid gap-5 px-5 py-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <TextField
                id="name"
                label="Restaurant name"
                required
                autoFocus
                autoComplete="organization"
                placeholder="Blue Fig Cafe"
                value={form.name}
                onChange={(event) => update({ name: event.target.value })}
                error={errors.name}
                disabled={mutation.isPending}
              />
            </div>
            <TextField
              id="phone"
              label="Phone"
              type="tel"
              autoComplete="tel"
              placeholder="Optional"
              value={form.phone}
              onChange={(event) => update({ phone: event.target.value })}
              error={errors.phone}
              disabled={mutation.isPending}
            />
            <SelectField
              id="timezone"
              label="Timezone"
              required
              hint="IANA timezone name."
              value={form.timezone}
              onChange={(event) => update({ timezone: event.target.value })}
              error={errors.timezone}
              disabled={mutation.isPending}
            >
              {IANA_TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </SelectField>
            <SelectField
              id="planId"
              label="Plan"
              hint="Optional — a tenant can be created before a plan is agreed. Limits warn, they never block."
              value={form.planId}
              onChange={(event) => update({ planId: event.target.value })}
              disabled={mutation.isPending || plansQuery.isPending}
            >
              <option value="">No plan</option>
              {(plansQuery.data ?? []).map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </SelectField>
            <SelectField
              id="currency"
              label="Currency"
              required
              value={form.currency}
              onChange={(event) => update({ currency: event.target.value })}
              error={errors.currency}
              disabled={mutation.isPending}
            >
              {CURRENCIES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </SelectField>
            <div className="sm:col-span-2">
              <TextAreaField
                id="address"
                label="Address"
                placeholder="Optional"
                value={form.address}
                onChange={(event) => update({ address: event.target.value })}
                error={errors.address}
                disabled={mutation.isPending}
              />
            </div>
            {isOwner ? (
              <div className="sm:col-span-2">
                <LogoPicker value={logoFile} onChange={setLogoFile} disabled={mutation.isPending} />
              </div>
            ) : null}
          </div>
        </section>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button onClick={() => navigate('/cp/tenants')} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            {mutation.isPending ? 'Creating…' : 'Create tenant'}
          </Button>
        </div>
      </form>
    </>
  );
}
